/**
 * The Walliyo console tab, without React.
 *
 * The same draft-then-Save shape as the app tab, over the staff catalogue.
 * What differs is who it can be about — everyone, or one employee — and what
 * a row's source can say, because a console feature can come from a role.
 */

import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type {
  SaveStaffFeaturesRequest,
  StaffFeatureListItem,
  StaffFeatureSubject,
} from "@wallyo/contracts/console/configuration";

import { t } from "../../i18n";
import { REASON_MIN_LENGTH } from "../team/team-writes";
import { cleanUuid, isCompleteUuid, type Draft } from "./feature-save-model";

/**
 * What the subject bar holds while someone is typing into it.
 *
 * One employee, so one box. There is no group to fall back to: this screen
 * exists for the per-person override, and "what can all staff do?" is the
 * role grid's question.
 */
export type StaffSubjectFields = { uuid: string };

export function emptyStaffSubject(): StaffSubjectFields {
  return { uuid: "" };
}

/** Null until the whole number is in: a half-typed UUID is nobody. */
export function staffSubjectOf({ uuid }: StaffSubjectFields): StaffFeatureSubject | null {
  return isCompleteUuid(uuid) ? { uuid: Number(cleanUuid(uuid)) } : null;
}

export function sameStaffSubject(one: StaffFeatureSubject, other: StaffFeatureSubject): boolean {
  return one.uuid === other.uuid;
}

/**
 * Where the row's answer came from, in the reader's words.
 *
 * "By role" names the roles, because that is the useful part: it says which
 * role to change if the answer should be different for everybody holding it,
 * rather than for this one person.
 */
export function staffSourceLabel({ source, grantedBy }: StaffFeatureListItem): string {
  switch (source) {
    case "staffMember":
      return t("features.staffSource.override");
    case "staffRole":
      return grantedBy.length > 0
        ? t("features.staffSource.byRoleNamed", { roles: grantedBy.join(", ") })
        : t("features.staffSource.byRole");
    case "allStaff":
      // Nothing writes this scope; the resolver still reads it, so the screen
      // can name it rather than call somebody's value a default.
      return t("features.staffSource.allStaff");
    default:
      return t("features.source.default");
  }
}

/** An override is the only answer set on this person, so the only one to clear. */
export function isOverride({ source }: StaffFeatureListItem): boolean {
  return source === "staffMember";
}

export function staffNotEditableLabel({ editable, notEditable }: StaffFeatureListItem): string | null {
  if (editable) return null;
  return notEditable === "own_account" ? t("features.locked.ownAccount") : t("features.locked.grant");
}

export function shownStaffValue(feature: StaffFeatureListItem, draft: Draft): boolean {
  const drafted = draft[feature.settingId];
  if (drafted === undefined) return feature.value;
  // Cleared: it goes back to whatever the roles say, which the server decides.
  return drafted ?? false;
}

export function isStaffChanged(feature: StaffFeatureListItem, draft: Draft): boolean {
  return draft[feature.settingId] !== undefined;
}

export function changedStaffFeatures(
  features: readonly StaffFeatureListItem[],
  draft: Draft,
): readonly StaffFeatureListItem[] {
  return features.filter((feature) => isStaffChanged(feature, draft));
}

/** Flip one row. Back to what it loaded as, and already its own value, is no change. */
export function withStaffToggle(draft: Draft, feature: StaffFeatureListItem): Draft {
  const next = !shownStaffValue(feature, draft);
  const { [feature.settingId]: _dropped, ...rest } = draft;
  if (next === feature.value && isOverride(feature)) return rest;
  return { ...rest, [feature.settingId]: next };
}

export function saveStaffRequest(
  subject: StaffFeatureSubject,
  reason: string,
  features: readonly StaffFeatureListItem[],
  draft: Draft,
): SaveStaffFeaturesRequest {
  return {
    subject,
    reason: reason.trim(),
    changes: changedStaffFeatures(features, draft).map(({ settingId, valueVersion }) => ({
      settingId: settingId as StaffFeatureId,
      value: draft[settingId] ?? null,
      ...(valueVersion === undefined ? {} : { version: valueVersion }),
    })),
  };
}

export function staffSaveProblem(
  reason: string,
  features: readonly StaffFeatureListItem[],
  draft: Draft,
): string | null {
  const changed = changedStaffFeatures(features, draft);
  if (changed.length === 0) return t("features.save.nothing");
  if (changed.some((feature) => !feature.editable)) return t("features.save.locked");
  if (reason.trim().length < REASON_MIN_LENGTH) return t("features.save.reasonNeeded");
  return null;
}
